// Small byte helpers for crypto/ and store/ (I-1a Batch 3). Every buffer handed
// to WebCrypto is a plain ArrayBuffer-backed Uint8Array (`Bytes`), which is what
// the DOM `BufferSource` type requires.

export type Bytes = Uint8Array<ArrayBuffer>

/** getRandomValues fills at most 65,536 bytes per call. */
const RANDOM_CHUNK = 65536

export function randomBytes(length: number): Bytes {
  const bytes = new Uint8Array(length)
  for (let offset = 0; offset < length; offset += RANDOM_CHUNK) {
    crypto.getRandomValues(bytes.subarray(offset, Math.min(offset + RANDOM_CHUNK, length)))
  }
  return bytes
}

export function utf8(text: string): Bytes {
  return new TextEncoder().encode(text)
}

/** Strict UTF-8 decode: malformed bytes throw instead of becoming U+FFFD. */
export function fromUtf8(bytes: Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
}

export function concatBytes(...parts: readonly Uint8Array[]): Bytes {
  const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0))
  let offset = 0
  for (const part of parts) {
    out.set(part, offset)
    offset += part.length
  }
  return out
}

/** Copy into a fresh ArrayBuffer-backed array (e.g. bytes read back from storage). */
export function copyBytes(bytes: Uint8Array): Bytes {
  return new Uint8Array(bytes)
}

export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false
  }
  let diff = 0
  for (let i = 0; i < a.length; i++) {
    diff |= a[i]! ^ b[i]!
  }
  return diff === 0
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function fromHex(hex: string): Bytes {
  if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(hex)) {
    throw new Error('malformed hex')
  }
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return out
}

/** Base64 for small values that travel inside an encrypted JSON payload (e.g. a photo key). */
export function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) {
    binary += String.fromCharCode(b)
  }
  return btoa(binary)
}

export function fromBase64(text: string): Bytes {
  return Uint8Array.from(atob(text), (c) => c.charCodeAt(0))
}

/** Best-effort wipe of a buffer we own. JavaScript cannot promise memory is cleared (stated honestly). */
export function wipe(bytes: Uint8Array): void {
  bytes.fill(0)
}
